import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Tooltip } from "@mui/material";
import GraphicEqOutlinedIcon from "@mui/icons-material/GraphicEqOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import Papa from "papaparse";
import { get, off, onValue, ref } from "firebase/database";
import { useParams } from "react-router-dom";

import { database } from "../../firebase";
import MetricCard from "../ui/MetricCard";
import RecordDialog from "../ui/RecordDialog";
import EmptyState from "../ui/EmptyState";
import useSnack from "../ui/useSnack";
import Chart from "../charts/Chart";
import { baseChartOption, lineSeries } from "../charts/chartTheme";
import { saveChartImage } from "../../utils/saveChartImage";
import { EM_DASH, describe, formatRelative, formatTimestamp, latestOf } from "../../utils/format";

const PROXY = "https://proxyserver-ten.vercel.app/proxy-csv?path=";

/* The three channels are named after their emitters, so their colours follow
   the physical channel rather than the categorical slot order. Because red and
   green are the weakest pair under protanopia, each channel also carries a
   distinct line type — identity never rests on hue alone here. */
const CHANNELS = [
  { key: "red", label: "Red", column: "PPG_RED", color: "#e34948", lineType: "solid" },
  { key: "ir", label: "Infrared", column: "PPG_IR", color: "#4a3aa7", lineType: "dashed" },
  { key: "green", label: "Green", column: "PPG_GREEN", color: "#008300", lineType: "dotted" },
];

const storagePathFromUrl = (url) => {
  const matches = String(url || "").match(/\/o\/(.+)\?/);
  return matches?.[1] ? decodeURIComponent(matches[1]) : null;
};

const PPG = ({ title = "Photoplethysmography", extraActions, menu }) => {
  const { watchId } = useParams();
  const chartRef = useRef(null);
  const { snack, showSnack } = useSnack();

  const [records, setRecords] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [statusDescription, setStatusDescription] = useState("");
  const [trace, setTrace] = useState({ samples: [], red: [], ir: [], green: [] });

  useEffect(() => {
    const watchRef = ref(database, `Health/${watchId}/ppg`);

    const handler = onValue(watchRef, (snapshot) => {
      if (!snapshot.exists()) {
        setRecords([]);
        setSelectedRecord(null);
        return;
      }

      const all = Object.entries(snapshot.val())
        .map(([ts, record]) => ({ ...record, timestamp: ts }))
        .filter((record) => record.url && record.timestamp)
        .sort((a, b) => Number(b.timestamp) - Number(a.timestamp));

      setRecords(all);
      setSelectedRecord((previous) => {
        if (!previous) return all[0] ?? null;
        return all.find((r) => r.timestamp === previous.timestamp) ?? all[0] ?? null;
      });
    });

    return () => off(watchRef, "value", handler);
  }, [watchId]);

  useEffect(() => {
    if (!selectedRecord?.timestamp) {
      setStatusDescription("");
      return;
    }

    get(
      ref(
        database,
        `Health/${watchId}/ppg/${selectedRecord.timestamp}/ppgStatusDescription`
      )
    )
      .then((snapshot) => setStatusDescription(snapshot.exists() ? snapshot.val() : ""))
      .catch((error) => {
        console.error("Error fetching ppgStatusDescription:", error);
        setStatusDescription("");
      });
  }, [selectedRecord, watchId]);

  const loadTrace = useCallback(async (url) => {
    const path = storagePathFromUrl(url);
    if (!path) return;

    setLoading(true);
    try {
      const response = await fetch(`${PROXY}${encodeURIComponent(path)}`);
      const csvText = await response.text();

      const parsed = Papa.parse(csvText, {
        header: true,
        dynamicTyping: true,
        skipEmptyLines: true,
      });

      setTrace({
        samples: parsed.data.map((_, index) => index + 1),
        red: parsed.data.map((row) => row.PPG_RED),
        ir: parsed.data.map((row) => row.PPG_IR),
        green: parsed.data.map((row) => row.PPG_GREEN),
      });
    } catch (error) {
      console.error("Error fetching or parsing PPG CSV:", error);
      setTrace({ samples: [], red: [], ir: [], green: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedRecord?.url) loadTrace(selectedRecord.url);
  }, [selectedRecord, loadTrace]);

  const combined = useMemo(
    () => [...trace.red, ...trace.ir, ...trace.green],
    [trace]
  );
  const stats = useMemo(() => describe(combined, 0), [combined]);

  const accent = CHANNELS[2].color;

  const buildOption = () =>
    baseChartOption({
      categories: trace.samples,
      series: CHANNELS.map((channel) =>
        lineSeries({
          name: channel.label,
          data: trace[channel.key],
          color: channel.color,
          lineType: channel.lineType,
          area: false,
          smooth: 0.15,
          width: 1.6,
          showSymbol: false,
        })
      ),
      showLegend: true,
      xName: "Sample",
      tooltipFormatter: (params) => {
        if (!params?.length) return "";
        const rows = params
          .map(
            (p) =>
              `<div style="display:flex;gap:8px;justify-content:space-between">
                 <span>${p.marker} ${p.seriesName}</span>
                 <strong>${p.value ?? "—"}</strong>
               </div>`
          )
          .join("");
        return `Sample ${params[0].axisValue}${rows}`;
      },
    });

  const handleExport = async () => {
    const result = await saveChartImage(chartRef, watchId, "ppg");
    showSnack(
      result.ok
        ? "Chart saved — find it on the Files page."
        : "Could not export the chart image.",
      result.ok ? "success" : "error"
    );
  };

  return (
    <>
      <MetricCard
        accent={accent}
        icon={<GraphicEqOutlinedIcon />}
        title={title}
        meta={
          <>
            <span className="wg-chip wg-chip--accent">
              {selectedRecord ? "Record loaded" : "No records"}
            </span>
            {selectedRecord && (
              <span className="wg-chip">
                {formatTimestamp(selectedRecord.timestamp)}
              </span>
            )}
            {/* The channels are named and colour-keyed by the chart legend, so
                repeating them here only made this header taller than the rest. */}
          </>
        }
        menu={menu}
        actions={
          <>
            {extraActions}
            <Tooltip title="Download a PNG and archive it under Files">
              <span>
                <Button
                  size="small"
                  startIcon={<DownloadOutlinedIcon />}
                  onClick={handleExport}
                  disabled={!trace.samples.length}
                >
                  Export
                </Button>
              </span>
            </Tooltip>
            <Button
              size="small"
              startIcon={<HistoryOutlinedIcon />}
              onClick={() => setSelectedRecord(latestOf(records))}
              disabled={!records.length}
            >
              Latest
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setDialogOpen(true)}
              disabled={!records.length}
            >
              Select record
            </Button>
          </>
        }
        note={statusDescription}
        stats={[
          {
            label: "Samples",
            value: trace.samples.length || EM_DASH,
            meta: "Points per channel",
          },
          { label: "Amplitude", value: stats.range, meta: "Across all channels" },
          { label: "Average", value: stats.avg, meta: "Across all channels" },
          {
            label: "Recorded",
            value: selectedRecord ? formatRelative(selectedRecord.timestamp) : EM_DASH,
            small: true,
            meta: selectedRecord?.displayName || "Unassigned",
          },
        ]}
        loading={loading}
        empty={
          !loading && !selectedRecord ? (
            <EmptyState
              icon={<GraphicEqOutlinedIcon />}
              title="Select a PPG capture"
              copy="Load a record to compare the red, infrared and green waveforms."
              action={
                <Button
                  variant="contained"
                  onClick={() => setDialogOpen(true)}
                  disabled={!records.length}
                >
                  Select a record
                </Button>
              }
            />
          ) : null
        }
      >
        <Chart
          buildOption={buildOption}
          chartRef={chartRef}
          height={300}
          deps={[trace]}
        />
      </MetricCard>

      <RecordDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSelect={(record) => {
          setSelectedRecord(record);
          setDialogOpen(false);
        }}
        records={records}
        accent={accent}
        title="PPG captures"
        getTitle={(record) => record.displayName || "PPG capture"}
        getMeta={(record) =>
          `${formatTimestamp(record.timestamp)} · ${formatRelative(record.timestamp)}`
        }
        isSelected={(record) => selectedRecord?.timestamp === record.timestamp}
      />

      {snack}
    </>
  );
};

export default PPG;
